/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { FC } from "react";
import { createEventEmitter, emitEvent, removeAllEventListeners } from "@adapptio/lib-shared";

export interface IFloatingPanelRect {
	x?: number;
	y?: number;
	width?: number;
	height?: number;
}

export interface IFloatingPanelParams {
	className?: string;
	title?: React.ReactNode;
	defaultRect?: IFloatingPanelRect;
}

export type FloatingPanelID = string;

/**
 * Dialog component properties
 */
export interface IFloatingPanelComponentProps<TFloatingPanelModeInstance extends FloatingPanelModel> {
	floatingPanelModel: TFloatingPanelModeInstance;
}

/**
 * Dialog component type
 */
export type TFloatingPanelComponent<TFloatingPanelModeInstance extends FloatingPanelModel> = FC<
	IFloatingPanelComponentProps<TFloatingPanelModeInstance>
>;

/**
 * Type helper to return floating panel open params
 */
export type TGetFloatingPanelOpenParams<TFloatingPanelModel> = TFloatingPanelModel extends FloatingPanelModel<
	infer A
>
	? A
	: never;

export interface IFloatingPanelModelConstructor<
	TFloatingPanelModel extends FloatingPanelModel,
	Params extends IFloatingPanelParams = TGetFloatingPanelOpenParams<TFloatingPanelModel>
> {
	/**
	 * View constructor
	 */
	new (id: FloatingPanelID, params: Params): TFloatingPanelModel;
}

export abstract class FloatingPanelModel<TOpenParams extends IFloatingPanelParams = unknown> {
	/** Panel ID */
	protected readonly id: FloatingPanelID;

	/** Open params */
	protected readonly params: TOpenParams;

	/** If the panel was closed */
	private isClosed: boolean;

	/** Function called when the panel is closed - internally bound to resolve promise */
	private closeResolveCallback: () => void = null;

	/** Promise resolved when the panel is closed */
	private readonly closePromise: Promise<void> = null;

	/** Rect - position and size in UI */
	private rect: IFloatingPanelRect;

	public onRectChange = createEventEmitter();

	/**
	 * Panel constructor
	 *
	 * @param id Panel ID
	 * @param params Open params
	 */
	constructor(id: FloatingPanelID, params: TOpenParams) {
		this.id = id;
		this.params = params;
		this.rect = params.defaultRect;

		this.closePromise = new Promise((resolve) => {
			this.closeResolveCallback = resolve;
		});

		this.init();
	}

	/**
	 * Returns dialog ID
	 */
	public getId(): FloatingPanelID {
		return this.id;
	}

	/**
	 * Returns open params
	 */
	public getParams(): TOpenParams {
		return this.params;
	}

	/**
	 * Initializes the dialog instance
	 */
	protected init(): void {
		// Optional method
	}

	/**
	 * Returns promise that is resolved when the panel is closed
	 * @returns
	 */
	public getClosePromise(): Promise<void> {
		return this.closePromise;
	}

	/**
	 * Closes the dialog
	 * To prevent close for some reason override this method and call parent's one when you want to close the dialog.
	 */
	public close(): void {
		if (this.isClosed) {
			return;
		}

		this.isClosed = true;
		this.closeResolveCallback();
	}

	/**
	 * Returns if the dialog has been closed
	 */
	public wasClosed(): boolean {
		return this.isClosed;
	}

	/**
	 * Disposes dialog
	 */
	public dispose(): void {
		removeAllEventListeners(this.onRectChange);
	}

	/**
	 * Returns react component to render dialog contents
	 */
	abstract getComponent(): TFloatingPanelComponent<FloatingPanelModel<TOpenParams>>;

	/** Rect - Position and Size */
	public getRect(): IFloatingPanelRect {
		return this.rect;
	}

	public updateRect({ width, height, x, y }: IFloatingPanelRect): void {
		this.rect = { ...this.rect, width, height, x, y };
		this.emitRectChange();
	}

	private emitRectChange(): void {
		emitEvent(this.onRectChange);
	}
}
